import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import './IsogridMaterial';
import { MathType, ThreeElements, useFrame } from '@react-three/fiber';
import { isMesh } from '@slope/three';
import { IsogridMaterialType } from './IsogridMaterial';

const _box3 = /* @__PURE__ */ new THREE.Box3();

export type IsogridProps = ThreeElements['mesh'] & {
  mesh?: React.RefObject<THREE.Mesh>;
  color?: MathType<THREE.Color>;
  radius?: number;
  lineThickness?: number;
  lineDensity?: number;
  theta?: number;
  opacity?: number;
  depthWrite?: boolean;
  animationSpeed?: number;
  polygonOffsetFactor?: number;
  debug?: boolean;
};

export const Isogrid: React.FC<IsogridProps> = ({
  mesh,
  radius = 5,
  color = '#ffffff',
  depthWrite,
  opacity = 1,
  lineThickness = 1,
  lineDensity = 10,
  polygonOffsetFactor = -1,
  animationSpeed = 0,
  theta,
  debug = false,
  children,
  ...restProps
}) => {
  const meshRef = useRef<THREE.Mesh>(null!);
  const mateRef = useRef<IsogridMaterialType>(null!);
  const parentRef = useRef<THREE.Mesh | undefined>(undefined);

  useFrame((_, deltaTime) => {
    // Check and update parent reference
    const currentParent = mesh?.current ?? meshRef.current?.parent;
    if (isMesh(currentParent) && parentRef.current !== currentParent) {
      parentRef.current = currentParent;

      // Update bounding box only when parent changes
      if (currentParent && mateRef.current) {
        currentParent.updateWorldMatrix(true, false);
        _box3.setFromObject(currentParent, true);
        mateRef.current.uniforms.boxMin.value.copy(_box3.min);
        mateRef.current.uniforms.boxMax.value.copy(_box3.max);
      }
    }

    // Update animation time
    if (mateRef.current && animationSpeed) {
      mateRef.current.uniforms.time.value += deltaTime * animationSpeed;
    }
  });

  return (
    <isogridMaterial
      ref={mateRef}
      transparent
      depthTest={false}
      polygonOffset
      polygonOffsetFactor={polygonOffsetFactor}
      opacity={opacity}
      depthWrite={depthWrite}
      lineThickness={lineThickness}
      lineDensity={lineDensity}
      theta={theta}
      color={color}
      {...restProps}
    />
  );
};
